import { useCallback } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import LiftingEquipmentForm from "src/components/pages/Settings/YachtSetUp/LiftingEquipmentForm/LiftingEquipmentForm";
import YachtSetupSectionHeader from "src/components/pages/Settings/YachtSetUp/YachtSetupSectionHeader";
import Loader from "src/components/shared/Loader/Loader";
import { useGetApi, usePostApi } from "src/hooks";
import { type ILink } from "src/interfaces";
import { PageContainer } from "src/styled";
import { getYachtId } from "src/utils";
import { Notification } from "src/utils/Notification";

import {
  type ILiftingEquipmentForm,
  type ILiftingEquipmentItem,
} from "./LiftingEquipment.types";

const emptyItem: ILiftingEquipmentItem = {
  name: "",
  description: "",
  certificates: [""],
  distinct: "",
};

interface ILiftingEquipment {
  list: ILiftingEquipmentItem[];
  id?: string;
  lastUpdatedBy?: string;
  lastUpdatedUtc?: string;
  lastUpdatedLt?: string;
  yachtid?: string;
}

const mapLiftingEquipment = (res: any): ILiftingEquipment | null => {
  const data = res?.list?.[0];
  if (!data) return { list: [] };

  const list = data?.data;
  if (!list || list.length === 0) return { list: [], ...data };

  return {
    list: list.map((item: ILiftingEquipmentItem) => ({
      ...item,
      certificates:
        item.certificates && item.certificates.length > 0
          ? item.certificates
          : [""],
    })),
  };
};

const LiftingEquipment = () => {
  const { data, isLoading: isGetLoading } = useGetApi({
    request: "get-lifting-equipment-list",
    body: { params: { yachtid: getYachtId() } },
    mapper: mapLiftingEquipment,
  });

  const getLinks = useCallback((links: ILink[]) => {
    return links.map((link, index) =>
      index === links.length - 1
        ? { ...link, title: "Lifting Equipment" }
        : link,
    );
  }, []);

  const { post, isLoading: isPostLoading } = usePostApi();

  const methods = useForm<ILiftingEquipmentForm>({
    values: {
      list: data?.list ?? [emptyItem],
    },
  });

  const { append, ...fieldArrayProps } = useFieldArray<ILiftingEquipmentForm>({
    control: methods.control,
    name: "list",
  });

  const handleAppend = () => {
    append({ ...emptyItem, certificates: [""] });
  };

  const action = { save: "save", remove: "remove" } as const;

  const onAction = async (
    formData: ILiftingEquipmentForm,
    actionType: keyof typeof action = "save",
  ): Promise<void> => {
    await post({
      request: "save-lifting-equipment-list",
      body: {
        params: {
          yachtid: getYachtId(),
          data: formData.list,
        },
      },
      onSuccess: () => {
        Notification(
          "Success",
          `Lifting Equipment has been successfully ${actionType === "save" ? "saved" : "removed"}.`,
          "green",
        );
      },
    });
  };

  const handleSave = methods.handleSubmit(
    async formData => await onAction(formData, action.save),
  );

  const handleRemove = async (index?: number | number[]) => {
    fieldArrayProps.remove(index);
    await methods.handleSubmit(
      async formData => await onAction(formData, action.remove),
    )();
  };

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <form onSubmit={handleSave}>
          <YachtSetupSectionHeader
            isSaving={isPostLoading}
            section="lifting"
            onAdd={handleAppend}
            customLinks={getLinks}
          />
          {!data || isGetLoading ? (
            <Loader />
          ) : (
            <LiftingEquipmentForm
              {...fieldArrayProps}
              remove={handleRemove}
              data={data?.list}
            />
          )}
        </form>
      </FormProvider>
    </PageContainer>
  );
};

export default LiftingEquipment;
